/* eslint-disable import/first */
/* eslint-disable no-use-before-define */
/* eslint-disable camelcase */
/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */
import './wdyr';
import 'intersection-observer';
import 'whatwg-fetch';
import './errorLogger';
import React from 'react';
import ReactDOM from 'react-dom';
import deepForceUpdate from 'react-deep-force-update';
import { parse } from 'qs';
import { createPath } from 'history/PathUtils';
import { HelmetProvider } from 'react-helmet-async';
import { toast, cssTransition } from 'react-toastify';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getLCP, getFID, getCLS } from 'web-vitals';

// app modules: actions
import { routeTransitionStart, routeTransitionEnd } from 'actions/router';
import { checkoutEvent } from 'actions/paywall';
import { closeAvatarMenu } from 'actions/avatarMenu';
import { setAdvertVisibility } from 'actions/adverts';
import { loadUser } from 'actions/user/loadUser';
import { getWebClientId } from 'actions/device';

import App from './components/App';
import Layout from './components/Layout';
import ErrorRouter from './components/ErrorRouter';
import createFetch from './createFetch';
import configureStore from './store/configureStore';
import createApolloClient, { cleanupApolloCache } from './core/createApolloClient';
import router from './router';

// app modules: services
import history from './services/history';
import { checkExperimentMembership } from './services/environment';
import SessionManager from './services/SessionManager';
import { sendEvent } from './services/analytics';
import { credentialsExist, getAuthToken } from './services/authTokenForWeb';
import i18nConfig from './i18n';

const LEGACY_PAYWALL_FRAGMENTS = ['#login', '#signup', '#subscribe', '#join', '#reset-password'];

// Enables critical path CSS rendering
// https://github.com/kriasoft/isomorphic-style-loader
const styleContext = {
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
};

i18n.use(initReactI18next).init({ ...i18nConfig, lng: window.App.language });

let lastUserRefresh = 0;
let store;
const apiConfig = {
  baseUrl: window.App.apiUrl,
  debug:
    window.App.state.config.environment === 'DEV' || window.App.state.config.environment === 'DEMO',
  tokenFactory: getAuthToken,
};

// Initialize a queue of commands for Ezoic placeholders
window.ezstandalone = window.ezstandalone || {};
window.ezstandalone.cmd = window.ezstandalone.cmd || [];

createApolloClient(apiConfig).then(apolloClient => {
  const { config, user: initialUser } = window.App.state;

  // Universal HTTP client
  const fetch = createFetch(window.fetch, apiConfig);

  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store = configureStore(window.App.state, {
    client: apolloClient,
    fetch,
    i18n,
  });

  // Grab the client ID
  store.dispatch(getWebClientId(config.baseUrl));

  // Load the user as soon as possible
  const sessionManager = new SessionManager(store);
  sessionManager.initiate();
  // The initial user state might show we are logged in when there's no auth credentials.
  // This happens if the user logs out and loads a page from the cache that was rendered when they were logged in.
  // We fetch the user from the API unauthenticated which will return a logged out user object,
  // thus fixing the state loaded from the cached page.
  if (credentialsExist() || initialUser.logged_in) {
    // First load the user from localstorage, if available
    store.dispatch(loadUser(true)).then(() =>
      // Trigger a network fetch to refresh the user data too
      store.dispatch(loadUser()).then(() => {
        sessionManager.handleUserLoaded();
      }),
    );
  } else {
    setTimeout(sessionManager.handleUserLoaded.bind(sessionManager), 0);
  }

  // Global (context) variables that can be easily accessed from any React component
  // https://facebook.github.io/react/docs/context.html
  const context = {
    fetch,
  };

  const container = document.getElementById('app');
  let currentLocation = history.location;

  let appInstance;
  let isInitialRender = config.platform !== 'app';

  // global namespace
  window.ckbk = {
    route: (event, to) => {
      if (!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)) {
        event.preventDefault();
        history.push(to);
      }
    },
  };

  // Switch off the native scroll restoration behavior and handle it manually
  // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
  if (window.history && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
  if (window.Paddle) {
    window.Paddle.Setup({
      vendor: store.getState().config.paywall.paddleVendorId,
      eventCallback: event => store.dispatch(checkoutEvent(event)),
    });
  }

  // Toast notifications...
  const Zoom = cssTransition({
    enter: 'Toastify__zoom-enter',
    exit: 'Toastify__zoom-exit',
    duration: [225, 500],
  });

  toast.configure({
    autoClose: 3000,
    draggable: false,
    closeOnClick: true,
    hideProgressBar: true,
    newestOnTop: true,
    pauseOnHover: true,
    pauseOnVisibilityChange: true,
    position: 'bottom-left',
    rtl: false,
    transition: Zoom,
  });

  // Re-render the app when window.location changes
  async function onLocationChange(location) {
    store.dispatch(routeTransitionStart(location));
    store.dispatch(closeAvatarMenu());
    store.dispatch(setAdvertVisibility(true));
    if (!isInitialRender) {
      sessionManager.handleLocationChange(location);
    }

    currentLocation = location;

    try {
      context.pathname = location.pathname.replace(/^\/w\//i, '/');

      context.query = parse(location.search, {
        ignoreQueryPrefix: true,
      });

      // Traverses the list of routes in the order they are defined until
      // it finds the first route that matches provided URL path string
      // and whose action method returns anything other than `undefined`.
      const route = await router.resolve({
        ...context,
        locationState: location.state || {},
        hash: location.hash,
        store,
        client: apolloClient,
        i18n,
      });
      if (!route) {
        return;
      }

      if (isInitialRender && window.App.isErrorPage && window.App.error) {
        route.component = (
          <Layout>
            <ErrorRouter error={window.App.error} />
          </Layout>
        );
      }

      // Prevent multiple page renders during the routing process
      if (currentLocation.key !== location.key) {
        return;
      }

      if (route.redirect) {
        history.replace(route.redirect, location.state);
        return;
      }
      // The /w/ path prefix - used to bypass universal links - should be removed from the URL as soon as we can so the user never sees it.
      if (location.pathname.startsWith('/w/')) {
        history.replace({ ...location, pathname: context.pathname }, location.state);
        return;
      }

      const renderReactApp = isInitialRender ? ReactDOM.hydrate : ReactDOM.render;
      appInstance = renderReactApp(
        <HelmetProvider>
          <App
            context={context}
            styleContext={styleContext}
            apolloClient={apolloClient}
            store={store}
          >
            {route.component}
          </App>
        </HelmetProvider>,
        container,
        () => {
          // Google Analytics tracking. Don't send 'pageview' event after
          // the initial rendering, as it was already sent
          sendEvent({
            event: 'virtualPageView',
            pagePath: createPath(location),
            pageTitle: route.title,
            platform: config.platform,
          });

          if (location.hash) {
            const target = document.getElementById(location.hash.substr(1));
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }

          if (isInitialRender) {
            const elem = document.getElementById('css');
            if (elem) elem.parentNode.removeChild(elem);

            // If there have been any calls to Ezoic from rendering placeholders we now need to call enable() and display()
            // Timeout to allow effects in EzoicPlaceholder.jsx to complete first.
            const percentageToRunEzoic = store.getState().config.adverts.ezoicPercentage;
            const ezoicEnabled = checkExperimentMembership('ezoic', percentageToRunEzoic);
            if (ezoicEnabled) {
              setTimeout(() => {
                if (window.ezstandalone.cmd.length) {
                  window.ezstandalone.cmd.push(() => {
                    // Runs after Ezoic JS has loaded
                    window.ezstandalone.enable();
                    window.ezstandalone.display();
                  });
                }
              }, 0);
            }

            isInitialRender = false;
            return;
          }

          let scrollY = 0;
          const targetHash = location.hash.substr(1);

          if (targetHash) {
            const target = document.getElementById(targetHash);
            if (target) {
              scrollY = window.scrollY + target.getBoundingClientRect().top;
            }
          }

          const handleRequestScroll = () => {
            requestAnimationFrame(() => {
              window.scrollTo(0, scrollY);

              window.removeEventListener('requestEnded', handleRequestScroll);
            });
          };

          if (scrollY !== 0) {
            if (location.pathname.includes('/books/')) {
              window.addEventListener('requestEnded', handleRequestScroll);
            } else {
              setTimeout(() => {
                // it was necessary to wrap this line in `requestAnimationFrame` to be compatible with
                // `react-list` component
                requestAnimationFrame(() => {
                  // Restore the scroll position if it was saved into the state
                  // or scroll to the given #hash anchor
                  // or scroll to top of the page
                  window.scrollTo(0, scrollY);
                });
              }, 1000);
            }
          }

          cleanupApolloCache(apolloClient);

          // Update ezoic placeholders
          if (window.ezstandalone.enabled) {
            setTimeout(() => {
              window.ezstandalone.refresh();
            }, 0);
          }
        },
      );
    } catch (error) {
      if (__DEV__) {
        throw error;
      }

      console.error(error);
    } finally {
      store.dispatch(
        routeTransitionEnd({
          ...location,
        }),
      );
      // Backwards compatibility with old paywall #fragment URLs
      if (LEGACY_PAYWALL_FRAGMENTS.includes(location.hash)) {
        history.push(`/${location.hash.slice(1)}`, { returnTo: location });
      }
    }
  }

  const handleVisibilityChange = () => {
    const now = new Date().getTime();
    if (document.visibilityState === 'visible' && now > lastUserRefresh + 5000) {
      store.dispatch(loadUser());
      lastUserRefresh = now;
    }
  };

  // Handle client-side navigation by using HTML5 History API
  // For more information visit https://github.com/mjackson/history#readme
  history.listen(onLocationChange);
  onLocationChange(currentLocation);
  document.addEventListener('visibilitychange', handleVisibilityChange, false);
  document.addEventListener('click', e => {
    if (
      e.target.tagName === 'A' &&
      e.target.href &&
      e.target.href.startsWith(config.baseUrl) &&
      !e.target.target &&
      !e.defaultPrevented &&
      // Let the browser handle modified clicks (ie, cmd-click to open in new tab)
      !(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey)
    ) {
      try {
        const url = new URL(e.target.href);
        e.preventDefault();
        history.push(`${url.pathname}${url.search}`);
      } catch (err) {
        // unparsable URL? ignore
      }
    }
  });

  // Enable Hot Module Replacement (HMR)
  if (module.hot && !window.Cypress) {
    module.hot.accept('./router', () => {
      if (appInstance && appInstance.updater.isMounted(appInstance)) {
        // Force-update the whole tree, including components that refuse to update
        deepForceUpdate(appInstance);
      }

      onLocationChange(currentLocation);
    });
  }
  // Allow cypress to do client side routing.
  if (window.Cypress) {
    window.handleOpenURL = history.push;
  }
});

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('./sw.js')
      .then(() => console.info('Service worker registered'))
      .catch(err => console.error('Service worker failed to register', err));
  });
}

// Performance monitoring
getCLS(console.info);
getFID(console.info);
getLCP(console.info);
